import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import {
    EditorCellFocusTarget,
    useEditorCellFocus,
} from "../useEditorCellFocus";
import { scrollNestedRowIntoView } from "./scrollNestedRowIntoView";

interface UseNestedEditorIssueFocusParams {
    issues: EditorCellFocusTarget[];
    tableRef: RefObject<ViewTableHandle | null>;
    // Clears the active search + column filters so the offending row is part of
    // the rendered tree before we expand to + focus its cell.
    clearFilters?: () => void;
    // When given, filters are cleared only for a row the filters hide.
    isRowVisible?: (rowId: string) => boolean;
    // Issues carry draft ids; the merged workspace view keys rows by display key.
    resolveRowId?: (draftId: string) => string;
    // The business filter reset is URL state and commits a render later than
    // clearFilters; focusing before that render lands loses focus on remount.
    isRowBusinessHidden?: (draftId: string) => boolean;
    resetBusinessFilter?: () => void;
}

export interface NestedEditorIssueFocus {
    currentIssueIndex: number;
    safeIssueIndex: number;
    focusIssue: (index: number) => void;
}

// Owns the issue-bar navigation for the nested editor: tracks the current issue
// index and, on navigate, clears filters, expands the offending row's ancestors
// and scrolls it into view, then enters edit mode on its cell. The expand runs
// on the first `onBeforeFocus` (once the filter-clear re-render has landed and
// the row is back in the model), not on the gesture and not on every frame:
// rewriting expanded state each frame overwrites ViewTable's own expansion.
export const useNestedEditorIssueFocus = ({
    issues,
    tableRef,
    clearFilters,
    isRowVisible,
    resolveRowId,
    isRowBusinessHidden,
    resetBusinessFilter,
}: UseNestedEditorIssueFocusParams): NestedEditorIssueFocus => {
    const [currentIssueIndex, setCurrentIssueIndex] = useState(0);

    const hasExpandedRef = useRef(false);
    const { requestFocus } = useEditorCellFocus(({ rowId }) => {
        if (!hasExpandedRef.current) {
            hasExpandedRef.current = true;
            tableRef.current?.expandRowAndAncestors(rowId);
        }
        scrollNestedRowIntoView(tableRef, rowId, "center");
    }, resolveRowId);

    const [deferredTarget, setDeferredTarget] =
        useState<EditorCellFocusTarget | null>(null);
    useEffect(() => {
        if (
            deferredTarget === null ||
            isRowBusinessHidden?.(deferredTarget.rowId)
        ) {
            return;
        }
        setDeferredTarget(null);
        requestFocus(deferredTarget);
    }, [deferredTarget, isRowBusinessHidden, requestFocus]);

    const safeIssueIndex = Math.min(
        currentIssueIndex,
        Math.max(issues.length - 1, 0),
    );

    const focusIssue = useCallback(
        (index: number) => {
            if (issues.length === 0) {
                return;
            }
            const clampedIndex = Math.min(
                Math.max(index, 0),
                issues.length - 1,
            );
            const issue = issues[clampedIndex];
            setCurrentIssueIndex(clampedIndex);
            hasExpandedRef.current = false;
            if (!isRowVisible?.(issue.rowId)) {
                clearFilters?.();
            }
            const target = { rowId: issue.rowId, field: issue.field };
            if (resetBusinessFilter && isRowBusinessHidden?.(issue.rowId)) {
                resetBusinessFilter();
                setDeferredTarget(target);
                return;
            }
            setDeferredTarget(null);
            requestFocus(target);
        },
        [
            clearFilters,
            isRowVisible,
            isRowBusinessHidden,
            resetBusinessFilter,
            issues,
            requestFocus,
        ],
    );

    return { currentIssueIndex, safeIssueIndex, focusIssue };
};
